/*
 * Which language to open in, as data. No Vue and no vue-i18n, so the awkward part — matching
 * whatever tag a browser hands back against the three languages this app has — is testable
 * without mounting anything.
 *
 * The order of preference is: a choice this browser remembers, then English. A choice stored on
 * the subject overrides the first of those when `/me` lands, which is what carries a choice from
 * a phone to a laptop. See docs/ROADMAP.md, Phase 9.
 *
 * The browser's own languages are **not** in that order. `detectLocale` used to put them second,
 * and the result was a first screen in a language nobody had chosen; English is the default now
 * and only an explicit choice moves off it.
 */

import type { Subject } from '@/api/types';

/**
 * English first because it is the fallback for everything unmatched, not because it is better.
 * Mirrors the API's `SUPPORTED_LOCALES`; the contract is what makes them agree.
 */
export const SUPPORTED_LOCALES = ['en', 'af', 'es'] as const;

/** The language, never the tag. `Subject['locale']` is the same union, minus the null. */
export type Locale = NonNullable<Subject['locale']>;

export const DEFAULT_LOCALE: Locale = 'en';

/** Namespaced like the session cookie, so a shared origin cannot collide with it. */
export const LOCALE_STORAGE_KEY = 'rs_locale';

/** Names each language in its own — nobody looking for Afrikaans is helped by "Afrikaans (AF)". */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  af: 'Afrikaans',
  es: 'Español',
};

/**
 * The one-time offer of another language, written *in* that language.
 *
 * Deliberately not in the message catalogues, for the same reason team names and game codes are
 * not: a line that says "also available in Afrikaans" has to be in Afrikaans, and a translated
 * key renders in whatever language the reader is already in — which is the language they are
 * being offered a way out of. Loading another locale's chunk to read one sentence would cost more
 * than the sentence.
 *
 * English is not in here. It is the default, so nobody is ever offered it: `detectOffer` only
 * ever returns a language the browser asked for and the reader is not already in.
 */
export const LANGUAGE_OFFERS: Record<'af' | 'es', { line: string; action: string }> = {
  af: { line: 'Hierdie bladsy is ook in Afrikaans beskikbaar.', action: 'Wissel na Afrikaans' },
  es: { line: 'Esta página también está disponible en español.', action: 'Cambiar a español' },
};

/**
 * A language worth offering, or null — and it is the *only* place this app reads the browser's
 * languages for anything but a currency.
 *
 * `bootLocale` ignores them on purpose: a first screen in a language nobody chose is worse than a
 * predictable one, and that reasoning stands. What it left was an Afrikaans reader whose only
 * route to Afrikaans was a 12px footer link at 3.26:1, three screens deep. This is the middle:
 * the app still opens in English and still changes language only because somebody changed it —
 * it just says the option is there.
 *
 * **Only while nothing is stored.** Storing a locale is what dismissing the offer does, so this
 * needs no second flag and no new browser-storage key to disclose on the privacy page.
 */
export function detectOffer(current: Locale, stored: Locale | null): 'af' | 'es' | null {
  if (stored !== null) return null;

  const asked = typeof navigator === 'undefined' ? [] : (navigator.languages ?? []);

  for (const tag of asked) {
    // `en-ZA` is English; the region is not a language, and af-ZA and es-AR are the cases here.
    const base = tag.toLowerCase().split('-')[0];
    if (base === current) return null;
    if (base === 'af' || base === 'es') return base;
  }

  return null;
}

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * The choice this browser remembers, or null if there is none.
 *
 * Null rather than the default, because "never chosen" and "chose English" are different: only
 * the first may be overridden by the subject's own choice when `/me` lands.
 */
export function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    // Safari in private mode throws rather than returning null, and iOS Safari is most of this
    // app's traffic. A browser that will not remember is not a browser that cannot read.
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Same again: the choice still applies to this page, it just will not outlive it.
  }
}
